import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { ProjectDocument } from '../../services/api';

interface DeleteDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  document: ProjectDocument | null;
}

const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  document,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleConfirm = async () => {
    if (isDeleting) return; // Prevent multiple clicks
    
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return; // Prevent closing during deletion
    setIsDeleting(false); // Reset state when dialog closes
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-document-dialog-title"
      aria-describedby="delete-document-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        id="delete-document-dialog-title"
        sx={{ pb: 1 }}
      >
        Delete Document from Index
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <DialogContentText 
          id="delete-document-dialog-description"
          sx={{ mb: 0 }}
        >
          Are you sure you want to delete "{document?.filename}" from this index? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ pt: 0, pb: 2, px: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          autoFocus 
          variant="contained" 
          color="error" 
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDocumentDialog;
